import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import { AppConsts } from '@shared/AppConsts';
import { ApiException } from '@shared/service-proxies/service-proxies';

// API responses that are not client bugs. 500 is deliberately NOT here — it still reports.
//   0           request aborted (navigation away), offline, or API restarting during a deploy
//   400         business validation (UserFriendlyException) — ABP already shows the message
//   401/403     session expired / no permission — the ABP interceptor refreshes or redirects
//   502/503/504 API down or restarting — an infra issue, visible in the API's own monitoring
const IGNORED_API_STATUSES = [0, 400, 401, 403, 502, 503, 504];

function isExpectedHttpError(error: unknown): boolean {
    return (error instanceof HttpErrorResponse || error instanceof ApiException) && IGNORED_API_STATUSES.includes(error.status);
}

function initSentry(): void {
    // No DSN (e.g. local development) keeps Sentry disabled — nothing is sent.
    if (!AppConsts.sentryDsn) {
        return;
    }

    Sentry.init({
        dsn: AppConsts.sentryDsn,
        environment: AppConsts.sentryEnvironment,
        release: AppConsts.appVersion,
        tracesSampleRate: 0, // errors only — no performance tracing for now
        // Session Replay is DISABLED (24.08.2026). It was enabled on 19.08 with a 10%
        // sample of ordinary sessions plus every error session, but usage blew past the
        // plan's included 50 replays/month (134 by mid-cycle) and started billing
        // pay-as-you-go at ~$0.00375/replay. The 10% ordinary-session sample was the
        // cost driver, so the whole integration is turned off for now.
        //
        // The sample rates below are pre-set for an ERRORS-ONLY re-enable: keep
        // replaysSessionSampleRate at 0 (never record error-free sessions — that was the
        // expensive part) and only capture a replay when a session actually errors. They
        // have no effect while replayIntegration is commented out; they only take effect
        // once it is added back to the integrations array.
        replaysSessionSampleRate: 0, // 0 = never sample ordinary (error-free) sessions
        replaysOnErrorSampleRate: 1.0, // capture a replay only when the session hits an error
        integrations: [
            // Session Replay disabled — see the note above. To re-enable (errors only, at
            // the sample rates above), uncomment this integration. maskAllText/blockAllMedia
            // must stay true: this is a CRM, so client names, phones and emails must never
            // appear in recordings. Watch Replays usage in Sentry billing after re-enabling.
            // Sentry.replayIntegration({
            //     maskAllText: true,
            //     blockAllMedia: true,
            // }),
        ],
        ignoreErrors: [
            // Angular Material icon fetch aborted/failed with HTTP status 0. The SVG
            // asset exists; status 0 means the request was cancelled (typically the
            // component was destroyed on navigation), so it is benign noise. A real
            // missing icon still reports because it fails with a 404, not a 0.
            /Error retrieving icon.*: 0 /i,
            // Google Maps' WebGL vector renderer emitting an internal, third-party
            // "Hung Labeler: Labeler is hung waiting for resources" error. Not
            // actionable from our code and depends on the client GPU/browser.
            /Hung Labeler/i,
            // Same renderer, internal "EpochMismatch" state error — third-party, not actionable.
            /EpochMismatch/,
            // The proxy returned an HTML 502/503 page and abp-ng2's interceptor tried to
            // JSON.parse it (Chrome / Safari / Firefox wording). Cause is the API being down.
            /Unexpected token '<'/,
            /Unrecognized token '<'/,
            /JSON\.parse: unexpected character at line 1 column 1/,
        ],
        beforeSend: (event, hint) => (isExpectedHttpError(hint?.originalException)
            ? null
            : event),
    });
}

export { initSentry };
